import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  FormHelperText,
  Checkbox,
  Avatar,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faEdit, faAddressCard, faLocationDot, faUser } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../AppDataTable";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles, StatusActive } from "../../themes/TableStyles";
import { StandardHeader } from "../../themes/StandardHeader";
import { resolveProfileImageSrc } from "../../utils/profileImage";

const DLDEmployeeManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [officeTypes, setOfficeTypes] = useState([]);
  const [offices, setOffices] = useState([]);
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOfficeType, setFilterOfficeType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    DLDEmployeeID: "",
    FirstName: "",
    LastName: "",
    FirstName2: "",
    LastName2: "",
    Gender: "M",
    OfficeType: "",
    OfficeID: "",
    DepartmentName: "",
    Position: "",
    Email: "",
    Phone: "",
    Address: "",
    ProfileImage: "",
    Remark: "",
    Status: "1",
    Password: "",
    ConfirmPassword: "",
    RoleID: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    NewPassword: "",
    ConfirmPassword: "",
  });
  const [resetPasswordError, setResetPasswordError] = useState("");

  const roleName =
    roles.find((role) => String(role.RoleID) === String(formData.RoleID))?.RoleName || "";

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสเจ้าหน้าที่",
      selector: (row) => row.DLDEmployeeID,
      sortable: true,
      minWidth: "130px",
      wrap: true,
    },
    {
      name: "ชื่อ-นามสกุล",
      selector: (row) => `${row.FirstName} ${row.LastName}`,
      sortable: true,
      grow: 2,
      minWidth: "160px",
      wrap: true,
    },
    {
      name: "หน่วยงาน",
      selector: (row) => {
        const officeType = officeTypes.find((t) => t.OfficeTypeID === row.OfficeType);
        return officeType ? officeType.OfficeTypeName : row.OfficeType || "-";
      },
      sortable: true,
      grow: 2,
      minWidth: "160px",
      wrap: true,
      hide: "md",
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusActive status={row.Status} />,
      sortable: true,
      width: "110px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.Edit
          icon={faEdit}
          onClick={() => handleEdit(row)}
          label="แก้ไข"
          size={{ base: "xs", md: "sm" }}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "110px",
    },
  ];

  const fetchOfficeTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setOfficeTypes(response.OfficeTypeList || []);
      }
    } catch (error) {
      console.error("Error fetching office types:", error);
    }
  };

  const fetchOffices = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setOffices(response.OfficeList || []);
        setFilteredOffices(response.OfficeList || []);
      }
    } catch (error) {
      console.error("Error fetching offices:", error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getRolesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setRoles(response.RolesList || []);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const fetchEmployees = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDEmployeeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.employeeList || [];
        setEmployees(data);
        setFilteredEmployees(data);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchEmployees();
      fetchOfficeTypes();
      fetchOffices();
      fetchRoles();
    }
  }, [sessionId]);

  useEffect(() => {
    if (formData.OfficeType) {
      const filtered = offices.filter(office => office.OfficeType === formData.OfficeType);
      setFilteredOffices(filtered);
      if (!filtered.some(office => office.OfficeID === formData.OfficeID)) {
        setFormData(prev => ({ ...prev, OfficeID: "" }));
      }
    } else {
      setFilteredOffices(offices);
    }
  }, [formData.OfficeType, offices]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value, filterOfficeType, filterStatus);
  };

  const handleOfficeTypeFilter = (e) => {
    const value = e.target.value;
    setFilterOfficeType(value);
    applyFilters(searchTerm, value, filterStatus);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    applyFilters(searchTerm, filterOfficeType, value);
  };

  const applyFilters = (search, officeType, status) => {
    let filtered = employees;

    if (search) {
      const searchTerms = search.split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) => {
        const officeTypeName = officeTypes.find((t) => t.OfficeTypeID === row.OfficeType)?.OfficeTypeName || row.OfficeType || "";
        return searchTerms.every((term) =>
          [
            row.DLDEmployeeID.toLowerCase(),
            row.FirstName.toLowerCase(),
            row.LastName.toLowerCase(),
            officeTypeName.toLowerCase(),
            row.Status ? "active" : "inactive",
          ].some((field) => field.includes(term))
        );
      });
    }

    if (officeType) {
      filtered = filtered.filter((row) => row.OfficeType === officeType);
    }

    if (status) {
      filtered = filtered.filter((row) => (status === "1" ? row.Status : !row.Status));
    }

    setFilteredEmployees(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilterOfficeType("");
    setFilterStatus("");
    setFilteredEmployees(employees);
  };

  const handleAddNew = () => {
    setFormData({
      DLDEmployeeID: "",
      FirstName: "",
      LastName: "",
      FirstName2: "",
      LastName2: "",
      Gender: "M",
      OfficeType: "",
      OfficeID: "",
      DepartmentName: "",
      Position: "",
      Email: "",
      Phone: "",
      Address: "",
      ProfileImage: "",
      Remark: "",
      Status: "1",
      Password: "",
      ConfirmPassword: "",
      RoleID: "",
    });
    setIsEditing(false);
    setShowForm(true);
    setErrors({});
    setPrivacyChecked(false);
  };

  const handleEdit = async (row) => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDEmployeeByID",
        data: { DLDEmployeeID: row.DLDEmployeeID },
        sessionId: sessionId || "",
      });

      if (response.result === "success" && response.employee) {
        const employeeData = response.employee;

        // ดึงข้อมูลผู้ใช้เพื่อแสดงสิทธิ์การใช้งานที่ผูกกับบัญชี
        const userResponse = await apiService({
          method: "POST",
          url: "/getUserByProfileID",
          data: { ProfileID: row.DLDEmployeeID, ProfileType: "Employee" },
          sessionId: sessionId || "",
        });

        const userData = userResponse.result === "success" && userResponse.user ? userResponse.user : {};

        setFormData({
          ...employeeData,
          Status: employeeData.Status ? "1" : "0",
          Password: "",
          ConfirmPassword: "",
          RoleID: userData.RoleID || "",
        });
        setIsEditing(true);
        setShowForm(true);
        setErrors({});
        setPrivacyChecked(true);
      } else {
        alert("ไม่พบข้อมูลเจ้าหน้าที่");
      }
    } catch (error) {
      console.error("Error fetching employee data:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูลเจ้าหน้าที่");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "DLDEmployeeID":
        if (!value) {
          newErrors[name] = "กรุณากรอกรหัสเจ้าหน้าที่";
        } else if (value.length > 30) {
          newErrors[name] = "รหัสเจ้าหน้าที่ต้องไม่เกิน 30 ตัวอักษร";
        } else {
          delete newErrors[name];
        }
        break;
      case "FirstName":
      case "LastName":
      case "OfficeType":
      case "OfficeID":
      case "DepartmentName":
      case "Phone":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      case "Email":
        if (!value) {
          newErrors[name] = "กรุณากรอกอีเมล";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors[name] = "รูปแบบอีเมลไม่ถูกต้อง";
        } else {
          delete newErrors[name];
        }
        break;
      case "Password":
        if (!value) {
          newErrors[name] = "กรุณากรอกรหัสผ่าน";
        } else if (value.length < 6) {
          newErrors[name] = "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร";
        } else {
          delete newErrors[name];
        }
        break;
      case "ConfirmPassword":
        if (!value) {
          newErrors[name] = "กรุณายืนยันรหัสผ่าน";
        } else if (value !== formData.Password) {
          newErrors[name] = "รหัสผ่านไม่ตรงกัน";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        delete newErrors[name];
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.DLDEmployeeID &&
      formData.DLDEmployeeID.length <= 30 &&
      formData.FirstName &&
      formData.LastName &&
      formData.OfficeType &&
      formData.OfficeID &&
      formData.DepartmentName &&
      formData.Email &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email) &&
      formData.Phone &&
      // รหัสผ่านกรอกเฉพาะตอนสร้างใหม่ การเปลี่ยนรหัสผ่านของเจ้าหน้าที่เดิมใช้ปุ่มรีเซ็ตรหัสผ่าน
      (isEditing ||
        (formData.Password &&
          formData.Password.length >= 6 &&
          formData.ConfirmPassword === formData.Password)) &&
      Object.keys(errors).length === 0 &&
      privacyChecked
    );
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const apiUrl = isEditing ? "/updateDLDEmployee" : "/registerDLDEmployee";
      const { ProfileImage, Password, ConfirmPassword, ...employeeFields } = formData;
      const submitData = {
        ...employeeFields,
        Status: formData.Status === "1",
      };

      if (!isEditing) {
        submitData.ProfileImage = "";
        submitData.Password = Password;
      }

      const response = await apiService({
        method: "POST",
        url: apiUrl,
        data: submitData,
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        // Backend registerDLDEmployee และ updateDLDEmployee ได้จัดการ User table ให้แล้ว
        // ไม่จำเป็นต้องเรียก API เพิ่มเติม
        alert(isEditing ? "อัปเดตข้อมูลเจ้าหน้าที่สำเร็จ!" : "สร้างข้อมูลเจ้าหน้าที่สำเร็จ!");
        setShowForm(false);
        fetchEmployees();
      }
    } catch (error) {
      console.error("Error saving employee:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      DLDEmployeeID: "",
      FirstName: "",
      LastName: "",
      FirstName2: "",
      LastName2: "",
      Gender: "M",
      OfficeType: "",
      OfficeID: "",
      DepartmentName: "",
      Position: "",
      Email: "",
      Phone: "",
      Address: "",
      ProfileImage: "",
      Remark: "",
      Status: "1",
      Password: "",
      ConfirmPassword: "",
      RoleID: "",
    });
    setErrors({});
    setPrivacyChecked(false);
  };

  const openResetPassword = () => {
    setResetPasswordData({ NewPassword: "", ConfirmPassword: "" });
    setResetPasswordError("");
    setIsResetPasswordOpen(true);
  };

  const handleResetPassword = async () => {
    if (resetPasswordData.NewPassword.length < 6) {
      setResetPasswordError("รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร");
      return;
    }
    if (resetPasswordData.NewPassword !== resetPasswordData.ConfirmPassword) {
      setResetPasswordError("รหัสผ่านไม่ตรงกัน");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/resetUserPasswordByProfile",
        data: {
          ProfileID: formData.DLDEmployeeID,
          ProfileType: "Employee",
          NewPassword: resetPasswordData.NewPassword,
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        setIsResetPasswordOpen(false);
        alert("รีเซ็ตรหัสผ่านเรียบร้อยแล้ว กรุณาแจ้งรหัสผ่านใหม่ให้เจ้าหน้าที่ทราบ");
      } else {
        setResetPasswordError(response.error || "ไม่สามารถรีเซ็ตรหัสผ่านได้");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      setResetPasswordError(error.message || "ไม่สามารถรีเซ็ตรหัสผ่านได้");
    } finally {
      setIsLoading(false);
    }
  };

  // ไม่ลบข้อมูลเจ้าหน้าที่ออกจากฐานข้อมูล เพราะรหัสเจ้าหน้าที่ถูกอ้างอิงไว้ในเอกสารต่าง ๆ
  // เช่น ผู้อนุมัติใบเบิก ผู้รับวัคซีน และผู้บันทึกรายการ จึงใช้การปิดการใช้งานแทน
  const handleToggleStatus = async () => {
    const nextStatus = formData.Status !== "1";
    const confirmMessage = nextStatus
      ? "ยืนยันการเปิดการใช้งานเจ้าหน้าที่รายนี้ เจ้าหน้าที่จะกลับมาเข้าสู่ระบบได้อีกครั้ง"
      : "ยืนยันการปิดการใช้งานเจ้าหน้าที่รายนี้ เจ้าหน้าที่จะไม่สามารถเข้าสู่ระบบได้ แต่ข้อมูลและเอกสารที่เคยบันทึกไว้จะยังอยู่ในระบบ";

    if (!window.confirm(confirmMessage)) return;

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateDLDEmployee",
        data: {
          DLDEmployeeID: formData.DLDEmployeeID,
          Status: nextStatus,
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        alert(
          nextStatus
            ? "เปิดการใช้งานเจ้าหน้าที่สำเร็จ"
            : "ปิดการใช้งานเจ้าหน้าที่สำเร็จ"
        );
        setShowForm(false);
        fetchEmployees();
      }
    } catch (error) {
      console.error("Error updating employee status:", error);
      alert("เกิดข้อผิดพลาดในการปรับสถานะ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 3, sm: 4, md: 8, lg: 12 }}
      m={{ base: 2, md: 6, lg: 10 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        <StandardHeader
          title="รายการข้อมูลผู้ใช้งาน (เจ้าหน้าที่)"
          bgColor="iBlue.600"
          icon={faFileLines}
        />

        {/* ส่วนตัวกรอง */}
        <Flex
          w="100%"
          gap={3}
          direction={{ base: "column", lg: "row" }}
          align={{ base: "stretch", lg: "center" }}
          justify="space-between"
        >
          <Flex gap={3} wrap="wrap" align="center" flex="1" minW={0}>
            <InputGroup w={{ base: "100%", sm: "240px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาเจ้าหน้าที่..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <Select
              w={{ base: "100%", sm: "200px" }}
              value={filterOfficeType}
              onChange={handleOfficeTypeFilter}
              placeholder="เลือกประเภทสำนักงาน"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              {officeTypes.map((type) => (
                <option key={type.OfficeTypeID} value={type.OfficeTypeID}>
                  {type.OfficeTypeName}
                </option>
              ))}
            </Select>
            <Select
              w={{ base: "100%", sm: "160px" }}
              value={filterStatus}
              onChange={handleStatusFilter}
              placeholder="เลือกสถานะ"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Select>
            <IconButton.Clear
              onClick={handleClearSearch}
              label="ล้าง"
              w={{ base: "100%", sm: "auto" }}
            />
          </Flex>
          <IconButton.Add
            onClick={handleAddNew}
            label="เพิ่มข้อมูลใหม่"
            flexShrink={0}
            w={{ base: "100%", lg: "auto" }}
          />
        </Flex>

        {/* ส่วนลิสต์ */}
        {isLoading ? (
          <Spinner size="md" />
        ) : employees.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลเจ้าหน้าที่
          </Text>
        ) : filteredEmployees.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredEmployees}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>

      {/* Modal สำหรับเพิ่ม/แก้ไขข้อมูลเจ้าหน้าที่ */}
      <Modal
        isOpen={showForm}
        onClose={handleCancel}
        size={{ base: "full", md: "3xl", lg: "5xl", xl: "6xl" }}
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxH={{ base: "100vh", md: "90vh" }} my={{ base: 0, md: 4 }}>
          <ModalHeader
            bg="iBlue.600"
            color="white"
            borderTopRadius={{ base: 0, md: "md" }}
            py={{ base: 3, md: 4 }}
          >
            <Flex justify="space-between" align="center" pr={8} gap={4}>
              <Heading size={{ base: "sm", md: "md" }} fontWeight="normal">
                {isEditing ? "แก้ไขข้อมูลเจ้าหน้าที่" : "เพิ่มข้อมูลเจ้าหน้าที่"}
              </Heading>
              {isEditing && (
                <Box flexShrink={0} textAlign="center">
                  <Avatar
                    size={{ base: "md", md: "lg" }}
                    src={resolveProfileImageSrc(formData.ProfileImage)}
                    name={
                      !formData.ProfileImage
                        ? `${formData.FirstName || ""} ${formData.LastName || ""}`.trim()
                        : undefined
                    }
                    icon={<FontAwesomeIcon icon={faUser} />}
                  />
                </Box>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={{ base: 4, md: 6 }} px={{ base: 3, md: 6 }}>
            {/* ข้อมูลทั่วไป */}
            <Box borderWidth={1} borderRadius="lg" boxShadow="md" p={{ base: 3, md: 5 }} mb={{ base: 4, md: 6 }}>
              <StandardHeader
                title="ข้อมูลทั่วไป"
                bgColor="iTeal.500"
                icon={faAddressCard}
                mb={{ base: 4, md: 5 }}
              />
              <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={{ base: 4, md: 6 }}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.DLDEmployeeID}>
                    <FormLabel>รหัสเจ้าหน้าที่</FormLabel>
                    <Input
                      name="DLDEmployeeID"
                      value={formData.DLDEmployeeID}
                      onChange={handleInputChange}
                      isDisabled={isEditing}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.DLDEmployeeID}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.FirstName}>
                    <FormLabel>ชื่อ</FormLabel>
                    <Input
                      name="FirstName"
                      value={formData.FirstName}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.FirstName}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.LastName}>
                    <FormLabel>นามสกุล</FormLabel>
                    <Input
                      name="LastName"
                      value={formData.LastName}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.LastName}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>ชื่อ (ภาษาอื่น)</FormLabel>
                    <Input
                      name="FirstName2"
                      value={formData.FirstName2}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>นามสกุล (ภาษาอื่น)</FormLabel>
                    <Input
                      name="LastName2"
                      value={formData.LastName2}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>เพศ</FormLabel>
                    <Select
                      name="Gender"
                      value={formData.Gender}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    >
                      <option value="M">ชาย</option>
                      <option value="F">หญิง</option>
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.Position}>
                    <FormLabel>ตำแหน่ง</FormLabel>
                    <Input
                      name="Position"
                      value={formData.Position}
                      onChange={handleInputChange}
                      colorScheme="iTeal"
                    />
                    <FormErrorMessage>{errors.Position}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/* ข้อมูลหน่วยงาน */}
            <Box borderWidth={1} borderRadius="lg" boxShadow="md" p={{ base: 3, md: 5 }} mb={{ base: 4, md: 6 }}>
              <StandardHeader
                title="ข้อมูลหน่วยงาน"
                bgColor="iBlue.500"
                icon={faLocationDot}
                mb={{ base: 4, md: 5 }}
              />
              <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={{ base: 4, md: 6 }}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.OfficeType}>
                    <FormLabel>ประเภทสำนักงาน</FormLabel>
                    <Select
                      name="OfficeType"
                      value={formData.OfficeType}
                      onChange={handleInputChange}
                      placeholder="เลือกประเภทสำนักงาน"
                      colorScheme="iBlue"
                    >
                      {officeTypes.map((type) => (
                        <option key={type.OfficeTypeID} value={type.OfficeTypeID}>
                          {type.OfficeTypeName}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.OfficeType}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.OfficeID}>
                    <FormLabel>สำนักงาน</FormLabel>
                    <Select
                      name="OfficeID"
                      value={formData.OfficeID}
                      onChange={handleInputChange}
                      placeholder="เลือกสำนักงาน"
                      colorScheme="iBlue"
                      isDisabled={!formData.OfficeType}
                    >
                      {filteredOffices.map((office) => (
                        <option key={office.OfficeID} value={office.OfficeID}>
                          {office.OfficeName}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.OfficeID}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.DepartmentName}>
                    <FormLabel>หน่วยงาน</FormLabel>
                    <Input
                      name="DepartmentName"
                      value={formData.DepartmentName}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                    <FormErrorMessage>{errors.DepartmentName}</FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/* ข้อมูลการติดต่อ */}
            <Box borderWidth={1} borderRadius="lg" boxShadow="md" p={{ base: 3, md: 5 }} mb={{ base: 4, md: 6 }}>
              <StandardHeader
                title="ข้อมูลการติดต่อ"
                bgColor="iBlue.500"
                icon={faLocationDot}
                mb={{ base: 4, md: 5 }}
              />
              <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={{ base: 4, md: 6 }}>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.Email}>
                    <FormLabel>อีเมล</FormLabel>
                    <Input
                      name="Email"
                      value={formData.Email}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                    <FormErrorMessage>{errors.Email}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired isInvalid={!!errors.Phone}>
                    <FormLabel>เบอร์โทรศัพท์</FormLabel>
                    <Input
                      name="Phone"
                      value={formData.Phone}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                    <FormErrorMessage>{errors.Phone}</FormErrorMessage>
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, sm: 2, lg: 3 }}>
                  <FormControl>
                    <FormLabel>ที่อยู่</FormLabel>
                    <Input
                      name="Address"
                      value={formData.Address}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, sm: 2, lg: 3 }}>
                  <FormControl>
                    <FormLabel>หมายเหตุ</FormLabel>
                    <Textarea
                      name="Remark"
                      value={formData.Remark}
                      onChange={handleInputChange}
                      colorScheme="iBlue"
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/* ชื่อผู้ใช้และรหัสผ่าน */}
            <Box borderWidth={1} borderRadius="lg" boxShadow="md" p={{ base: 3, md: 5 }} mb={{ base: 4, md: 6 }}>
              <StandardHeader
                title="ชื่อผู้ใช้และรหัสผ่าน"
                bgColor="iPurple.500"
                icon={faUser}
                mb={{ base: 4, md: 5 }}
              />
              <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={{ base: 4, md: 6 }}>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>ชื่อผู้ใช้ (รหัสเจ้าหน้าที่)</FormLabel>
                    <Input
                      value={formData.DLDEmployeeID}
                      isReadOnly
                      isDisabled
                      colorScheme="iPurple"
                    />
                    <FormHelperText>ใช้รหัสเจ้าหน้าที่ในการเข้าสู่ระบบ</FormHelperText>
                  </FormControl>
                </GridItem>
                {isEditing ? (
                  <GridItem>
                    <FormControl>
                      <FormLabel>รหัสผ่าน</FormLabel>
                      <IconButton.Edit
                        onClick={openResetPassword}
                        label="รีเซ็ตรหัสผ่าน"
                        w={{ base: "100%", sm: "auto" }}
                      />
                      <FormHelperText>
                        การเปลี่ยนรหัสผ่านแยกออกจากการแก้ไขข้อมูลทั่วไป
                      </FormHelperText>
                    </FormControl>
                  </GridItem>
                ) : (
                  <>
                    <GridItem>
                      <FormControl isRequired isInvalid={!!errors.Password}>
                        <FormLabel>รหัสผ่าน</FormLabel>
                        <Input
                          name="Password"
                          type="password"
                          value={formData.Password}
                          onChange={handleInputChange}
                          colorScheme="iPurple"
                        />
                        <FormErrorMessage>{errors.Password}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                    <GridItem>
                      <FormControl isRequired isInvalid={!!errors.ConfirmPassword}>
                        <FormLabel>ยืนยันรหัสผ่าน</FormLabel>
                        <Input
                          name="ConfirmPassword"
                          type="password"
                          value={formData.ConfirmPassword}
                          onChange={handleInputChange}
                          colorScheme="iPurple"
                        />
                        <FormErrorMessage>{errors.ConfirmPassword}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                  </>
                )}
                <GridItem>
                  <FormControl>
                    <FormLabel>สิทธิ์การใช้งาน</FormLabel>
                    <Text minH="40px" display="flex" alignItems="center" fontWeight="medium">
                      {roleName || "-"}
                    </Text>
                    <FormHelperText>กำหนดโดยผู้ดูแลระบบ ไม่สามารถแก้ไขจากหน้านี้</FormHelperText>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>สถานะการใช้งาน</FormLabel>
                    <HStack spacing={3} align="center" minH="40px">
                      <StatusActive status={formData.Status === "1"} />
                      <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                        {isEditing
                          ? "ปรับได้จากปุ่มปิด/เปิดการใช้งานด้านล่าง"
                          : "เจ้าหน้าที่ที่สร้างใหม่จะเปิดการใช้งานทันที"}
                      </Text>
                    </HStack>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>

            {/* นโยบายความเป็นส่วนตัว */}
            <Box px={2}>
              <Checkbox
                isChecked={privacyChecked}
                onChange={(e) => setPrivacyChecked(e.target.checked)}
                size={{ base: "md", md: "lg" }}
                colorScheme="iOrange"
                alignItems="flex-start"
              >
                <Text fontSize={{ base: "xs", md: "md" }}>
                  ฉันได้อ่านนโยบายความเป็นส่วนตัวและเข้าใจนโยบายความเป็นส่วนตัวของเราแล้ว
                  และยอมรับเงื่อนไขการใช้งาน รวมถึงนโยบายความเป็นส่วนตัวของเรา
                  และยอมรับการใช้งานของคุกกี้ และเทคโนโลยีต่างๆ ที่เกี่ยวข้อง
                </Text>
              </Checkbox>
            </Box>
          </ModalBody>
          <ModalFooter gap={2} flexWrap="wrap" justifyContent="flex-end">
            <IconButton.Close
              onClick={handleCancel}
              label="ปิด"
              flex={{ base: "1 1 100%", sm: "0 0 auto" }}
            />
            {isEditing &&
              (formData.Status === "1" ? (
                <IconButton.Cancel
                  onClick={handleToggleStatus}
                  label="ปิดการใช้งาน"
                  flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                />
              ) : (
                <IconButton.Approved
                  onClick={handleToggleStatus}
                  label="เปิดการใช้งาน"
                  flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                />
              ))}
            <IconButton.Save
              onClick={handleSave}
              isLoading={isLoading}
              flex={{ base: "1 1 100%", sm: "0 0 auto" }}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* รีเซ็ตรหัสผ่าน แยกออกจากการแก้ไขข้อมูลทั่วไป */}
      <Modal
        isOpen={isResetPasswordOpen}
        onClose={() => setIsResetPasswordOpen(false)}
        size={{ base: "xs", sm: "md" }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bgColor="iPurple.500" color="white" fontSize={{ base: "md", md: "lg" }}>
            รีเซ็ตรหัสผ่าน
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={{ base: 4, md: 6 }}>
            <VStack spacing={4} align="stretch">
              <Text fontSize={{ base: "sm", md: "md" }} color="gray.600">
                กำหนดรหัสผ่านใหม่ให้ {formData.FirstName} {formData.LastName} (
                {formData.DLDEmployeeID})
              </Text>
              <FormControl isRequired>
                <FormLabel>รหัสผ่านใหม่</FormLabel>
                <Input
                  type="password"
                  value={resetPasswordData.NewPassword}
                  onChange={(e) => {
                    setResetPasswordError("");
                    setResetPasswordData({
                      ...resetPasswordData,
                      NewPassword: e.target.value,
                    });
                  }}
                  colorScheme="iPurple"
                />
                <FormHelperText>ต้องมีอย่างน้อย 6 ตัวอักษร</FormHelperText>
              </FormControl>
              <FormControl isRequired isInvalid={!!resetPasswordError}>
                <FormLabel>ยืนยันรหัสผ่านใหม่</FormLabel>
                <Input
                  type="password"
                  value={resetPasswordData.ConfirmPassword}
                  onChange={(e) => {
                    setResetPasswordError("");
                    setResetPasswordData({
                      ...resetPasswordData,
                      ConfirmPassword: e.target.value,
                    });
                  }}
                  colorScheme="iPurple"
                />
                <FormErrorMessage>{resetPasswordError}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2} flexWrap="wrap" justifyContent="flex-end">
            <IconButton.Close
              onClick={() => setIsResetPasswordOpen(false)}
              label="ปิด"
              flex={{ base: "1 1 100%", sm: "0 0 auto" }}
            />
            <IconButton.Confirm
              onClick={handleResetPassword}
              isLoading={isLoading}
              label="บันทึกรหัสผ่านใหม่"
              flex={{ base: "1 1 100%", sm: "0 0 auto" }}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DLDEmployeeManagement;